import { Link } from 'react-router-dom';

import { PageHero } from '@/components/PageHero';
import { CalibrationPlot } from '@/components/fx/Schematics';
import { TerminalPanel } from '@/components/fx/TerminalPanel';
import { Reveal } from '@/components/fx/Reveal';
import { Headline } from '@/components/fx/Headline';
import { TiltCard } from '@/components/fx/TiltCard';
import type { RecordVals } from '@/lib/vals/record';

export function RecordPage({ v }: { v: RecordVals }) {
  return (
    <main id="wft-main" tabIndex={-1}>
      <PageHero
        eyebrow="Track record"
        title="Written down first. Scored later. Published either way."
        highlight={['Scored']}
        lede="A forecast that can be edited after the outcome is not a forecast. Every run is recorded with its horizon, quantiles, baseline and decoder before the outcome exists, and the ledger keeps both the hits and the misses."
        aside={
          <TerminalPanel mnemonic="REC" title="Calibration" sources={['GodEngine']}>
            <CalibrationPlot />
          </TerminalPanel>
        }
      />

      {/* Lifecycle */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            The life of one forecast
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Five states, one direction."
            highlight={['one', 'direction.']}
          />

          <ol className="lifecycle">
            {v.lifecycle.map((s, i) => (
              <Reveal as="li" key={s.stage} delay={i * 90} className="life">
                <span className="life__dot" style={{ background: s.dot }} aria-hidden="true" />
                <div className="life__card glass">
                  <div className="life__head">
                    <h3>{s.stage}</h3>
                    <code>{s.state}</code>
                  </div>
                  <p>{s.body}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Metrics */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            What is measured
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Calibration is not accuracy."
            highlight={['not', 'accuracy.']}
          />
          <div className="clusters">
            {v.metrics.map((m, i) => (
              <Reveal key={m.label} delay={i * 70}>
                <TiltCard className="cluster glass" max={3}>
                  <div className="cluster__head">
                    <h3>{m.label}</h3>
                    <span>—</span>
                  </div>
                  <p className="cluster__blurb">{m.body}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
          <Reveal as="p" className="note" delay={200}>
            Scores read as em dashes until a forecast resolves in this deployment. Pending forecasts
            are counted separately, so the sample size is never quietly inflated.
          </Reveal>
        </div>
      </section>

      {/* Rules */}
      <section className="section">
        <div className="shell split">
          <div className="split__lead">
            <Reveal as="p" className="eyebrow">
              A backtest is not a record
            </Reveal>
            <Headline
              as="h2"
              className="display display--section"
              text="Four rules that stop a record flattering itself."
              highlight={['flattering']}
            />
            <Reveal delay={280}>
              <Link to="/engine" className="btn btn--ghost">
                How the forecast is made
              </Link>
            </Reveal>
          </div>
          <div className="guards">
            {v.rules.map((r, i) => (
              <Reveal key={r.label} delay={i * 80}>
                <div className="guard glass">
                  <span className="guard__index">{r.label}</span>
                  <p className="guard__rule">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
